import { logger } from '@/utils/production-logger';
import { MessageType } from '@/types/assistant';
import { OpenAIMessage } from '@/types/openai';
import { 
  StreamingCallbacks, 
  StreamingStartEvent, 
  StreamingCompleteEvent,
  AiStreamingRequest,
  AiStreamingResult
} from './streaming/types';
import { ConnectionManager } from './streaming/connection-manager';
import { StreamingClient } from './streaming/streaming-client';

// Re-export types for backward compatibility
export type {
  StreamingCallbacks,
  StreamingStartEvent,
  StreamingCompleteEvent,
  AiStreamingRequest,
  AiStreamingResult
};

export class UnifiedStreamingService {
  /**
   * Enhanced streaming with race condition prevention
   */
  static async startStreaming(
    request: AiStreamingRequest,
    callbacks: StreamingCallbacks
  ): Promise<AiStreamingResult> {
    const { requestId } = request;

    console.log('[STREAMING RACE FIX] 🚀 Starting streaming with race condition prevention:', {
      requestId,
      sessionId: request.sessionId,
      userId: request.userId,
      topic: request.topic,
      language: request.language,
      mode: request.mode,
      includeMultiLanguage: request.includeMultiLanguage,
      timestamp: new Date().toISOString()
    });

    try {
      // Initialize connection
      const abortController = ConnectionManager.initializeConnection(requestId);

      // RACE CONDITION FIX: Create enhanced callbacks wrapper to prevent duplicates
      const wrappedCallbacks = this.createRaceConditionSafeCallbacks(callbacks, requestId);

      // RACE CONDITION FIX: Only call onStart immediately if NOT already fired
      if (!ConnectionManager.hasStartEventFired(requestId)) {
        const startEvent: StreamingStartEvent = {
          requestId,
          sessionId: request.sessionId,
          userId: request.userId,
          model: 'gpt-4',
          knowledgeUsed: false,
          contextTruncated: false,
          queryExpansionUsed: false,
          timestamp: Date.now()
        };

        console.log('[STREAMING RACE FIX] 📞 Calling onStart (client-side prevention):', {
          requestId,
          startEvent
        });

        if (wrappedCallbacks.onStart) {
          wrappedCallbacks.onStart(startEvent);
          ConnectionManager.markStartEventFired(requestId);
        }
      }

      await StreamingClient.attemptStreaming(request, wrappedCallbacks, abortController);
      
      console.log('[STREAMING RACE FIX] ✅ Streaming completed successfully:', {
        requestId
      });
      
      return {
        success: true,
        requestId,
        fallbackUsed: false
      };

    } catch (streamError) {
      const errorMessage = streamError instanceof Error ? streamError.message : String(streamError);
      
      // RACE CONDITION FIX: Better error analysis before fallback
      const shouldFallback = this.shouldTriggerFallback(streamError, requestId);
      
      console.warn('[STREAMING RACE FIX] ⚠️ Streaming error analysis:', {
        error: errorMessage,
        requestId,
        shouldFallback,
        errorType: streamError instanceof Error ? streamError.name : 'unknown',
        isAborted: streamError instanceof Error && streamError.name === 'AbortError'
      });

      if (shouldFallback) {
        console.log('[STREAMING RACE FIX] 🔄 Attempting fallback after genuine failure');
        try {
          return await this.attemptFallback(request, callbacks);
        } catch (fallbackError) {
          const fallbackErrorMessage = fallbackError instanceof Error ? fallbackError.message : String(fallbackError);
          console.error('[STREAMING RACE FIX] ❌ Both streaming and fallback failed:', {
            streamError: errorMessage,
            fallbackError: fallbackErrorMessage,
            requestId
          });

          if (callbacks.onError) {
            callbacks.onError(fallbackErrorMessage, requestId);
          }
          
          return {
            success: false,
            error: fallbackErrorMessage,
            requestId,
            fallbackUsed: true
          };
        }
      } else {
        console.log('[STREAMING RACE FIX] ⏹️ Not falling back - error was expected (abort/timeout)');
        return {
          success: false,
          error: errorMessage,
          requestId,
          fallbackUsed: false
        };
      }
    } finally {
      ConnectionManager.cleanup(requestId);
    }
  }

  // RACE CONDITION FIX: Create race-condition safe callback wrapper
  private static createRaceConditionSafeCallbacks(
    originalCallbacks: StreamingCallbacks,
    requestId: string
  ): StreamingCallbacks {
    return {
      onStart: (data: StreamingStartEvent) => {
        // Prevent duplicate onStart calls
        if (ConnectionManager.hasStartEventFired(requestId)) {
          console.log('[STREAMING RACE FIX] ⚠️ Duplicate onStart prevented:', {
            requestId,
            source: 'SSE-event'
          });
          return;
        }
        
        console.log('[STREAMING RACE FIX] 📞 onStart called (SSE event):', {
          requestId,
          model: data.model
        });
        
        ConnectionManager.markStartEventFired(requestId);
        if (originalCallbacks.onStart) {
          originalCallbacks.onStart(data);
        }
      },
      
      onContent: originalCallbacks.onContent,
      onComplete: originalCallbacks.onComplete,
      onError: originalCallbacks.onError,
      onUsage: originalCallbacks.onUsage
    };
  }

  // RACE CONDITION FIX: Better fallback decision logic
  private static shouldTriggerFallback(error: unknown, requestId: string): boolean {
    if (!(error instanceof Error)) {
      return true; // Unknown errors should trigger fallback
    }

    // Don't fallback for user-initiated aborts
    if (error.name === 'AbortError') {
      console.log('[STREAMING RACE FIX] Abort detected - no fallback needed');
      return false;
    }

    // Don't fallback for timeouts (they're expected for long responses)
    if (error.message.includes('timeout') || error.message.includes('Request timeout')) {
      console.log('[STREAMING RACE FIX] Timeout detected - no fallback needed');
      return false;
    }

    // Don't fallback if connection was successful but parsing failed
    if (error.message.includes('parseSSEStream') || error.message.includes('SSE')) {
      console.log('[STREAMING RACE FIX] SSE parsing issue - likely data received, no fallback');
      return false;
    }

    // Fallback for genuine network/server errors
    if (error.message.includes('HTTP') || error.message.includes('fetch')) {
      console.log('[STREAMING RACE FIX] Network/HTTP error - fallback appropriate');
      return true;
    }

    // Conservative: fallback for other unknown errors
    console.log('[STREAMING RACE FIX] Unknown error type - fallback as safety');
    return true;
  }

  private static async attemptFallback(
    request: AiStreamingRequest,
    callbacks: StreamingCallbacks
  ): Promise<AiStreamingResult> {
    logger.debug('Attempting non-streaming fallback', {
      requestId: request.requestId,
      component: 'UnifiedStreamingService'
    });

    try {
      const { sendAIMessage } = await import('./ai-service-wrapper');
      
      const messages: OpenAIMessage[] = request.conversationHistory.map(msg => ({
        role: msg.role as 'system' | 'user' | 'assistant',
        content: msg.content
      }));
      
      const fallbackRequest = {
        messages,
        topic: request.topic as 'guide' | 'performance' | 'trading_strategy' | 'advisor',
        sessionId: request.sessionId,
        userId: request.userId,
        language: request.language,
        includeMultiLanguage: request.includeMultiLanguage,
        mode: request.mode
      };

      const response = await sendAIMessage(fallbackRequest);

      // Simulate streaming events for fallback
      callbacks.onStart?.({
        requestId: request.requestId,
        sessionId: request.sessionId,
        userId: request.userId,
        model: response.model || 'fallback',
        knowledgeUsed: response.knowledgeUsed || false,
        contextTruncated: false,
        queryExpansionUsed: false
      });

      callbacks.onComplete?.({
        requestId: request.requestId,
        messageId: crypto.randomUUID(),
        content: response.content,
        suggestions: response.suggestions || [],
        model: response.model || 'fallback',
        usage: response.usage || { promptTokens: 0, completionTokens: 0, totalTokens: 0 },
        knowledgeUsed: response.knowledgeUsed || false,
        knowledgeResults: response.knowledgeResults || [],
        suggestionsSource: 'fallback',
        contextTruncated: false,
        queryExpansionUsed: false
      });

      logger.debug('Fallback completed successfully', {
        requestId: request.requestId,
        component: 'UnifiedStreamingService'
      });

      return {
        success: true,
        requestId: request.requestId,
        fallbackUsed: true
      };

    } catch (error) {
      logger.error('Fallback failed', {
        error: error instanceof Error ? error.message : String(error),
        requestId: request.requestId,
        component: 'UnifiedStreamingService'
      });

      throw error;
    }
  }

  // Delegate connection management methods to ConnectionManager
  static stopStreaming(requestId: string): void {
    logger.debug('Stopping streaming', {
      requestId,
      component: 'UnifiedStreamingService'
    });
    ConnectionManager.cleanup(requestId);
  }

  static stopAllStreaming(): void {
    ConnectionManager.stopAll();
  }

  static isStreaming(requestId: string): boolean {
    return ConnectionManager.isStreaming(requestId);
  }

  static getConnectionState(requestId: string): string | undefined {
    return ConnectionManager.getConnectionState(requestId);
  }

  static getActiveConnectionsCount(): number {
    return ConnectionManager.getActiveConnectionsCount();
  }

  static getConnectionDetails(requestId?: string) {
    return ConnectionManager.getConnectionDetails(requestId);
  }
}
